/** DOM builders for each broadcast graphic. Built once per instance; `update` touches only text/widths. */
import type { GraphicKind, MatchConfig, MatchEvent, MatchStats, TeamProfile, TeamSide, WorldSnapshot } from '../core/contracts';
import { FORMATION_LINES, POSITION_LABEL, esc, inkFor, minuteInHalf, minuteLabel, type PlayerRef } from './format';

export interface EventMemory {
  cards: Map<string, { type: 'yellow' | 'red'; secondYellow: boolean; matchClock: number }>;
  /** keyed by player coming ON */
  subs: Map<string, { offId: string | null; matchClock: number }>;
  lastVar: MatchEvent | null;
  goals: Map<string, MatchEvent>;
}

export interface BuildCtx {
  config: MatchConfig;
  players: Map<string, PlayerRef>;
  snapshot: WorldSnapshot;
  stats: MatchStats;
  memory: EventMemory;
}

export interface Built { el: HTMLElement; update?: (ctx: BuildCtx) => void }

const div = (cls: string, html: string, style = ''): HTMLElement => {
  const el = document.createElement('div');
  el.className = cls;
  if (style) el.setAttribute('style', style);
  el.innerHTML = html;
  return el;
};

const teamVars = (t: TeamProfile): string =>
  `--tc:${t.primaryColor};--tc2:${t.secondaryColor};--tink:${inkFor(t.primaryColor)};`;

const setText = (el: Element | null, s: string): void => { if (el && el.textContent !== s) el.textContent = s; };
const setWidth = (el: HTMLElement | null, pct: number): void => {
  if (!el) return;
  const v = `${Math.max(0, Math.min(100, pct)).toFixed(1)}%`;
  if (el.style.width !== v) el.style.width = v;
};

function splitName(name: string, shortName: string): { first: string; last: string } {
  const i = name.toUpperCase().lastIndexOf(shortName.toUpperCase());
  const first = i > 0 ? name.slice(0, i).trim() : '';
  return { first, last: shortName.toUpperCase() };
}

function sideOf(ctx: BuildCtx, subjectId: string | undefined): TeamSide {
  if (subjectId === 'away' || subjectId === ctx.config.away.id) return 'away';
  return 'home';
}

const teamOf = (ctx: BuildCtx, side: TeamSide): TeamProfile => (side === 'home' ? ctx.config.home : ctx.config.away);

const nameRow = (p: PlayerRef): string => {
  const { first, last } = splitName(p.profile.name, p.profile.shortName);
  return `<div class="sdg-lt-name">${first ? `<span>${esc(first)}</span>` : ''}<b>${esc(last)}</b></div>`;
};

// ---------------------------------------------------------------------------
// Lower thirds
// ---------------------------------------------------------------------------

function lowerThird(p: PlayerRef, lead: string, sub: string, extra = ''): HTMLElement {
  return div('', `
    <div class="sdg-lt">
      <div class="sdg-lt-lead">${lead}</div>
      <div class="sdg-lt-body">
        <div class="sdg-lt-main">${nameRow(p)}<div class="sdg-lt-crest"><span>${esc(p.team.shortName)}</span></div></div>
        <div class="sdg-lt-sub">${sub}</div>
      </div>
      ${extra}
    </div>`, teamVars(p.team));
}

function playerLowerThird(p: PlayerRef): Built {
  const s = p.profile.stats;
  const sub = `
    <span class="sdg-lt-pos">${esc(POSITION_LABEL[p.profile.position])}</span>
    <span class="sdg-lt-stat"><em>GOALS</em><b>${s.seasonGoals}</b></span>
    <span class="sdg-lt-stat"><em>ASSISTS</em><b>${s.seasonAssists}</b></span>
    <span class="sdg-lt-stat"><em>APPS</em><b>${s.appearances}</b></span>
    <span class="sdg-lt-stat"><em>AGE</em><b>${s.age}</b></span>`;
  return { el: lowerThird(p, `<span class="sdg-lt-num">${p.profile.number}</span>`, sub) };
}

function cardLowerThird(p: PlayerRef, ctx: BuildCtx): Built {
  const c = ctx.memory.cards.get(p.profile.id);
  const type = c?.type ?? 'yellow';
  const second = c?.secondYellow ?? false;
  const minute = c ? minuteLabel(c.matchClock) : minuteInHalf(ctx.snapshot.matchClock, ctx.snapshot.half);
  const icon = second
    ? `<span class="sdg-card is-yellow is-behind"></span><span class="sdg-card is-red"></span>`
    : `<span class="sdg-card is-${type}"></span>`;
  const label = second ? 'SECOND YELLOW · SENT OFF' : type === 'red' ? 'RED CARD · SENT OFF' : 'YELLOW CARD';
  const sub = `<span class="sdg-lt-tag is-${type}">${label}</span><span class="sdg-lt-min">${minute}</span>
    <span class="sdg-lt-pos">#${p.profile.number} · ${esc(POSITION_LABEL[p.profile.position])}</span>`;
  return { el: lowerThird(p, `<span class="sdg-card-wrap">${icon}</span>`, sub) };
}

function subLowerThird(on: PlayerRef, ctx: BuildCtx): Built {
  const rec = ctx.memory.subs.get(on.profile.id);
  const off = rec?.offId ? ctx.players.get(rec.offId) : undefined;
  const minute = rec ? minuteLabel(rec.matchClock) : minuteInHalf(ctx.snapshot.matchClock, ctx.snapshot.half);
  const row = (p: PlayerRef, dir: 'on' | 'off', d: number): string => `
    <div class="sdg-sub-row is-${dir}" style="--d:${d}ms">
      <i class="sdg-sub-arrow"></i><span class="sdg-sub-dir">${dir.toUpperCase()}</span>
      <span class="sdg-sub-num">${p.profile.number}</span>${nameRow(p)}
    </div>`;
  const el = div('', `
    <div class="sdg-sub">
      <div class="sdg-sub-head"><span class="sdg-sub-crest">${esc(on.team.shortName)}</span><b>SUBSTITUTION</b><span class="sdg-sub-min">${minute}</span></div>
      <div class="sdg-sub-rows">${row(on, 'on', 380)}${off ? row(off, 'off', 480) : ''}</div>
    </div>`, teamVars(on.team));
  return { el };
}

function goalLowerThird(p: PlayerRef, ctx: BuildCtx): Built {
  const side = p.team.side;
  const ev = ctx.memory.goals.get(p.profile.id);
  const scorer = ctx.stats.scorers.filter((s) => s.playerId === p.profile.id).at(-1);
  const minute = ev ? minuteInHalf(ev.matchClock, ctx.snapshot.half) : scorer ? minuteLabel(scorer.matchClock) : minuteInHalf(ctx.snapshot.matchClock, ctx.snapshot.half);
  const tally = ctx.stats.scorers.filter((s) => s.playerId === p.profile.id).length;
  const note = tally >= 3 ? 'HAT-TRICK' : tally === 2 ? 'SECOND OF THE GAME' : `${p.profile.stats.seasonGoals + Math.max(1, tally)} THIS SEASON`;
  const { home, away } = ctx.config;
  const el = div('', `
    <div class="sdg-goal-flash"><div class="sdg-goal-band"></div><b data-t="GOAL">GOAL</b></div>
    <div class="sdg-goal-panel">
      <div class="sdg-goal-tag"><b>GOAL</b><span>${minute}</span></div>
      <div class="sdg-goal-body">
        <div class="sdg-lt-main">${nameRow(p)}</div>
        <div class="sdg-goal-note">${note}</div>
      </div>
      <div class="sdg-goal-score">
        <span class="sdg-gs-team ${side === 'home' ? 'is-scorer' : ''}" style="${teamVars(home)}">${esc(home.shortName)}</span>
        <b class="sdg-gs-h ${side === 'home' ? 'is-scorer' : ''}">0</b><i></i>
        <b class="sdg-gs-a ${side === 'away' ? 'is-scorer' : ''}">0</b>
        <span class="sdg-gs-team ${side === 'away' ? 'is-scorer' : ''}" style="${teamVars(away)}">${esc(away.shortName)}</span>
      </div>
    </div>`, teamVars(p.team));
  const h = el.querySelector('.sdg-gs-h'), a = el.querySelector('.sdg-gs-a');
  return {
    el,
    update(c) { setText(h, String(c.snapshot.score.home)); setText(a, String(c.snapshot.score.away)); },
  };
}

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

type StatRow = { label: string; get: (s: MatchStats, side: TeamSide) => number; fmt?: (v: number) => string };
const STAT_ROWS: StatRow[] = [
  { label: 'POSSESSION', get: (s, t) => s[t].possessionPct, fmt: (v) => `${Math.round(v)}%` },
  { label: 'SHOTS', get: (s, t) => s[t].shots },
  { label: 'ON TARGET', get: (s, t) => s[t].shotsOnTarget },
  { label: 'EXPECTED GOALS', get: (s, t) => s[t].xg, fmt: (v) => v.toFixed(2) },
  { label: 'PASS ACCURACY', get: (s, t) => s[t].passAccuracyPct, fmt: (v) => `${Math.round(v)}%` },
  { label: 'CORNERS', get: (s, t) => s[t].corners },
  { label: 'FOULS', get: (s, t) => s[t].fouls },
];

function teamHead(t: TeamProfile, align: 'h' | 'a'): string {
  return `<div class="sdg-th is-${align}" style="${teamVars(t)}"><i class="sdg-chip"></i><b>${esc(t.shortName)}</b><span>${esc(t.name)}</span></div>`;
}

function matchStats(ctx: BuildCtx): Built {
  const { home, away } = ctx.config;
  const rows = STAT_ROWS.map((r, i) => `
    <div class="sdg-ms-row" style="--d:${320 + i * 70}ms">
      <b class="sdg-ms-v is-h"></b>
      <div class="sdg-ms-bar is-h"><i></i></div>
      <span class="sdg-ms-label">${r.label}</span>
      <div class="sdg-ms-bar is-a"><i></i></div>
      <b class="sdg-ms-v is-a"></b>
    </div>`).join('');
  const el = div('', `
    <div class="sdg-panel sdg-ms">
      <div class="sdg-panel-title"><span>${esc(ctx.config.competition)}</span><b>MATCH STATS</b></div>
      <div class="sdg-ms-head">${teamHead(home, 'h')}<div class="sdg-ms-score"><b class="sdg-ms-sh">0</b><i></i><b class="sdg-ms-sa">0</b></div>${teamHead(away, 'a')}</div>
      <div class="sdg-ms-rows">${rows}</div>
    </div>`);
  const rowEls = Array.from(el.querySelectorAll<HTMLElement>('.sdg-ms-row')).map((r) => ({
    vh: r.querySelector<HTMLElement>('.sdg-ms-v.is-h'), va: r.querySelector<HTMLElement>('.sdg-ms-v.is-a'),
    bh: r.querySelector<HTMLElement>('.sdg-ms-bar.is-h i'), ba: r.querySelector<HTMLElement>('.sdg-ms-bar.is-a i'), row: r,
  }));
  const sh = el.querySelector('.sdg-ms-sh'), sa = el.querySelector('.sdg-ms-sa');
  return {
    el,
    update(c) {
      setText(sh, String(c.snapshot.score.home)); setText(sa, String(c.snapshot.score.away));
      STAT_ROWS.forEach((r, i) => {
        const e = rowEls[i]!;
        const h = r.get(c.stats, 'home'), a = r.get(c.stats, 'away');
        const fmt = r.fmt ?? ((v: number) => String(Math.round(v)));
        setText(e.vh, fmt(h)); setText(e.va, fmt(a));
        const tot = h + a;
        setWidth(e.bh, tot > 0 ? (h / tot) * 100 : 0);
        setWidth(e.ba, tot > 0 ? (a / tot) * 100 : 0);
        e.vh?.classList.toggle('is-lead', h > a);
        e.va?.classList.toggle('is-lead', a > h);
      });
    },
  };
}

function possessionBar(ctx: BuildCtx): Built {
  const { home, away } = ctx.config;
  const el = div('', `
    <div class="sdg-pb">
      <span class="sdg-pb-label">POSSESSION</span>
      <div class="sdg-pb-track">
        <div class="sdg-pb-seg is-h" style="${teamVars(home)}"><b></b></div>
        <div class="sdg-pb-seg is-a" style="${teamVars(away)}"><b></b></div>
      </div>
    </div>`);
  const sh = el.querySelector<HTMLElement>('.sdg-pb-seg.is-h'), sa = el.querySelector<HTMLElement>('.sdg-pb-seg.is-a');
  const th = sh?.querySelector('b') ?? null, ta = sa?.querySelector('b') ?? null;
  return {
    el,
    update(c) {
      const h = c.stats.home.possessionPct, a = c.stats.away.possessionPct;
      const tot = h + a || 1;
      const hp = (h / tot) * 100;
      setWidth(sh, hp); setWidth(sa, 100 - hp);
      setText(th, `${Math.round(hp)}%`); setText(ta, `${100 - Math.round(hp)}%`);
    },
  };
}

function playerStatCard(p: PlayerRef): Built {
  const s = p.profile.stats;
  const { first, last } = splitName(p.profile.name, p.profile.shortName);
  const season = [['GOALS', s.seasonGoals], ['ASSISTS', s.seasonAssists], ['APPS', s.appearances], ['AGE', s.age]] as const;
  const cell = (k: string, v: string | number, d: number, cls = ''): string =>
    `<div class="sdg-psc-cell ${cls}" style="--d:${d}ms"><b>${v}</b><em>${k}</em></div>`;
  const el = div('', `
    <div class="sdg-panel sdg-psc">
      <div class="sdg-psc-hero">
        <span class="sdg-psc-num">${p.profile.number}</span>
        <div class="sdg-psc-id">
          <span class="sdg-psc-first">${esc(first)}</span><b class="sdg-psc-last">${esc(last)}</b>
          <span class="sdg-psc-meta">${esc(POSITION_LABEL[p.profile.position])} · ${esc(p.profile.stats.nationality)} · ${esc(p.team.name)}</span>
        </div>
      </div>
      <div class="sdg-psc-section"><span>THIS SEASON</span></div>
      <div class="sdg-psc-grid">${season.map(([k, v], i) => cell(k, v, 300 + i * 60)).join('')}</div>
      <div class="sdg-psc-section"><span>THIS MATCH</span></div>
      <div class="sdg-psc-grid is-live">
        ${cell('TOUCHES', '0', 560, 'm-touches')}${cell('PASSES', '0', 620, 'm-passes')}
        ${cell('SHOTS', '0', 680, 'm-shots')}${cell('KM RUN', '0.0', 740, 'm-km')}
      </div>
      <div class="sdg-psc-speed" style="--d:820ms"><em>TOP SPEED</em><div class="sdg-psc-speedbar"><i></i></div><b>0.0 <small>KM/H</small></b></div>
    </div>`, teamVars(p.team));
  const g = (c: string): Element | null => el.querySelector(`.${c} b`);
  const touches = g('m-touches'), passes = g('m-passes'), shots = g('m-shots'), km = g('m-km');
  const spBar = el.querySelector<HTMLElement>('.sdg-psc-speedbar i'), spText = el.querySelector('.sdg-psc-speed > b');
  const id = p.profile.id;
  return {
    el,
    update(c) {
      const m = c.stats.players[id];
      if (!m) return;
      setText(touches, String(m.touches)); setText(passes, String(m.passes)); setText(shots, String(m.shots));
      setText(km, m.distanceKm.toFixed(1));
      setWidth(spBar, (m.topSpeedKmh / 36) * 100);
      if (spText) { const v = `${m.topSpeedKmh.toFixed(1)} `; if (spText.firstChild?.textContent !== v && spText.firstChild) spText.firstChild.textContent = v; }
    },
  };
}

// ---------------------------------------------------------------------------
// Full-frame panels
// ---------------------------------------------------------------------------

function teamLineup(ctx: BuildCtx, side: TeamSide): Built {
  const t = teamOf(ctx, side);
  const lines = FORMATION_LINES[t.formation] ?? [4, 4, 2];
  const players = t.players.slice(0, 11);
  const dots: string[] = [];
  const dot = (i: number, x: number, y: number): void => {
    const p = players[i];
    if (!p) return;
    const gk = i === 0;
    dots.push(`<div class="sdg-lu-dot ${gk ? 'is-gk' : ''}" style="left:${x}%;top:${y}%;--d:${420 + i * 55}ms">
      <i>${p.number}</i><span>${esc(p.shortName)}</span></div>`);
  };
  dot(0, 8, 50);
  let idx = 1;
  lines.forEach((n, li) => {
    const x = 26 + (li * 64) / Math.max(1, lines.length - 1);
    for (let j = 0; j < n; j++) dot(idx++, x, ((j + 1) / (n + 1)) * 100);
  });
  const list = players.map((p, i) => `
    <li style="--d:${380 + i * 40}ms"><i>${p.number}</i><b>${esc(p.shortName)}</b><span>${p.position}</span></li>`).join('');
  const bench = t.bench.slice(0, 9).map((p) => `<span><i>${p.number}</i>${esc(p.shortName)}</span>`).join('');
  const el = div('', `
    <div class="sdg-panel sdg-lu">
      <div class="sdg-lu-side">
        <div class="sdg-lu-team"><i class="sdg-chip"></i><div><span>STARTING XI</span><b>${esc(t.name)}</b></div></div>
        <ol class="sdg-lu-list">${list}</ol>
        <div class="sdg-lu-foot"><span><em>FORMATION</em>${t.formation}</span><span><em>MANAGER</em>${esc(t.manager)}</span></div>
      </div>
      <div class="sdg-lu-pitch">
        <div class="sdg-lu-lines"><i class="hw"></i><i class="cc"></i><i class="box l"></i><i class="box r"></i><i class="six l"></i><i class="six r"></i></div>
        ${dots.join('')}
      </div>
      ${bench ? `<div class="sdg-lu-bench"><em>SUBSTITUTES</em>${bench}</div>` : ''}
    </div>`, teamVars(t));
  return { el };
}

function scorePanel(ctx: BuildCtx, kind: 'half_time_score' | 'full_time_score'): Built {
  const { home, away } = ctx.config;
  const scorers = (side: TeamSide): string => ctx.stats.scorers
    .filter((s) => s.team === side)
    .map((s) => {
      const p = ctx.players.get(s.playerId);
      return `<span>${esc(p?.profile.shortName ?? '—')} <em>${minuteLabel(s.matchClock)}</em></span>`;
    }).join('');
  const title = kind === 'half_time_score' ? 'HALF-TIME' : 'FULL-TIME';
  const el = div('', `
    <div class="sdg-panel sdg-sp">
      <div class="sdg-sp-title"><b>${title}</b><span>${esc(ctx.config.competition)}</span></div>
      <div class="sdg-sp-main">
        <div class="sdg-sp-team is-h" style="${teamVars(home)}"><i class="sdg-chip"></i><b>${esc(home.name)}</b></div>
        <div class="sdg-sp-score"><b class="sdg-sp-h">0</b><i></i><b class="sdg-sp-a">0</b></div>
        <div class="sdg-sp-team is-a" style="${teamVars(away)}"><b>${esc(away.name)}</b><i class="sdg-chip"></i></div>
      </div>
      <div class="sdg-sp-scorers"><div class="is-h">${scorers('home')}</div><div class="is-a">${scorers('away')}</div></div>
      <div class="sdg-sp-foot">
        <span><em>POSSESSION</em><b class="sdg-sp-pos"></b></span>
        <span><em>SHOTS</em><b class="sdg-sp-shots"></b></span>
        <span><em>xG</em><b class="sdg-sp-xg"></b></span>
        <span class="sdg-sp-venue">${esc(ctx.config.venue)}</span>
      </div>
    </div>`);
  const h = el.querySelector('.sdg-sp-h'), a = el.querySelector('.sdg-sp-a');
  const pos = el.querySelector('.sdg-sp-pos'), shots = el.querySelector('.sdg-sp-shots'), xg = el.querySelector('.sdg-sp-xg');
  return {
    el,
    update(c) {
      setText(h, String(c.snapshot.score.home)); setText(a, String(c.snapshot.score.away));
      setText(pos, `${Math.round(c.stats.home.possessionPct)} – ${Math.round(c.stats.away.possessionPct)}`);
      setText(shots, `${c.stats.home.shots} – ${c.stats.away.shots}`);
      setText(xg, `${c.stats.home.xg.toFixed(2)} – ${c.stats.away.xg.toFixed(2)}`);
    },
  };
}

function varCheck(ctx: BuildCtx): Built {
  const ev = ctx.memory.lastVar;
  const reason = (ev?.text ?? 'Checking possible penalty').toUpperCase();
  const el = div('', `
    <div class="sdg-var">
      <div class="sdg-var-badge"><b>VAR</b></div>
      <div class="sdg-var-body"><span class="sdg-var-k"><i></i>CHECK IN PROGRESS</span><b>${esc(reason)}</b></div>
    </div>`);
  return { el };
}

// ---------------------------------------------------------------------------

export function buildGraphic(kind: GraphicKind, subjectId: string | undefined, ctx: BuildCtx): Built | null {
  const player = subjectId ? ctx.players.get(subjectId) : undefined;
  switch (kind) {
    case 'player_lower_third': return player ? playerLowerThird(player) : null;
    case 'card_lower_third': return player ? cardLowerThird(player, ctx) : null;
    case 'sub_lower_third': return player ? subLowerThird(player, ctx) : null;
    case 'goal_lower_third': return player ? goalLowerThird(player, ctx) : null;
    case 'player_stat_card': return player ? playerStatCard(player) : null;
    case 'match_stats': return matchStats(ctx);
    case 'possession_bar': return possessionBar(ctx);
    case 'team_lineup': return teamLineup(ctx, sideOf(ctx, subjectId));
    case 'half_time_score':
    case 'full_time_score': return scorePanel(ctx, kind);
    case 'var_check': return varCheck(ctx);
    default: return null; // scorebug / clock / replay_bug are persistent elements
  }
}
